import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_HINT,
  evaluatePassword,
  validateNewPassword,
} from "@/lib/password-policy";

export const Route = createFileRoute("/intern_/setup")({
  head: () => ({
    meta: [
      { title: "Admin-Setup – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SetupPage,
});

type Result =
  | { kind: "ok"; status: string; email: string }
  | { kind: "err"; msg: string };

function SetupPage() {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("zugang@creategy.de");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setResult(null);

    const problem = validateNewPassword(password, confirm);
    if (problem) {
      setResult({ kind: "err", msg: problem });
      return;
    }
    if (!token.trim()) {
      setResult({ kind: "err", msg: "Bootstrap-Token fehlt." });
      return;
    }

    setBusy(true);
    try {
      const res = await fetch("/api/public/bootstrap-admin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-bootstrap-token": token.trim(),
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const text = await res.text();
      let payload: unknown = null;
      try {
        payload = JSON.parse(text);
      } catch {
        /* not json */
      }

      if (!res.ok) {
        const msg =
          (payload as { error?: string } | null)?.error ??
          text ??
          `HTTP ${res.status}`;
        setResult({ kind: "err", msg });
        return;
      }

      const p = payload as { status?: string; email?: string } | null;
      setResult({
        kind: "ok",
        status: p?.status ?? "ok",
        email: p?.email ?? email.trim(),
      });
      setPassword("");
      setConfirm("");
      setToken("");
    } catch (err) {
      setResult({
        kind: "err",
        msg: err instanceof Error ? err.message : "Unbekannter Fehler",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-extrabold">Admin-Setup</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Einmaliger Einrichtungs-Vorgang. Legt einen Admin-Account an oder setzt
            sein Passwort zurück. Bitte nach erfolgreichem Login das Token in den
            Lovable Cloud Secrets rotieren.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
            <div>
              <label htmlFor="token" className="block text-sm font-semibold">
                Bootstrap-Token
              </label>
              <input
                id="token"
                type="password"
                autoComplete="off"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                placeholder="Wert von ADMIN_BOOTSTRAP_TOKEN"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Zu finden unter Lovable Cloud → Secrets.
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
            </div>

            <div>
              <label htmlFor="pw" className="block text-sm font-semibold">
                Passwort <span className="text-muted-foreground">({PASSWORD_HINT})</span>
              </label>
              <input
                id="pw"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="password-requirements"
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
              <PasswordRequirements password={password} confirm={confirm} />
            </div>

            <div>
              <label htmlFor="pw2" className="block text-sm font-semibold">
                Passwort bestätigen
              </label>
              <input
                id="pw2"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
            </div>

            {result?.kind === "err" && (
              <p role="alert" className="text-sm text-destructive">
                {result.msg}
              </p>
            )}

            {result?.kind === "ok" && (
              <div className="rounded-md border border-border bg-secondary p-4 text-sm">
                <p className="font-semibold">Erfolgreich ({result.status})</p>
                <p className="mt-1 text-muted-foreground">
                  Account <span className="font-medium text-foreground">{result.email}</span> ist bereit.
                </p>
                <Link
                  to="/intern"
                  search={{ redirect: "/admin" }}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4 transition-opacity hover:opacity-80"
                >
                  Zum Login
                  <ArrowUpRight className="h-4 w-4" aria-hidden />
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !evaluatePassword(password, confirm).allMet}
              aria-describedby="password-requirements"
              className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {busy ? "Einen Moment…" : "Admin einrichten"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
